import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, X, FileSpreadsheet } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/authContext";

export type ExcelToolId = "to-csv" | "to-xlsx" | "merge" | "split";

interface Props {
  toolId: ExcelToolId;
  toolName: string;
  onClose: () => void;
}

const TOOL_CONFIG: Record<ExcelToolId, { endpoint: string; multi: boolean; accept: string; hint: string }> = {
  "to-csv":  { endpoint: "/api/tools/excel/to-csv",   multi: false, accept: ".xlsx,.xls,.xlsm,.xlsb", hint: "Upload an Excel file — the first sheet will be exported as CSV." },
  "to-xlsx": { endpoint: "/api/tools/excel/to-xlsx",  multi: false, accept: ".csv,.xls,.xlsm,.xlsb",  hint: "Upload a CSV or older Excel file to convert to .xlsx format." },
  "merge":   { endpoint: "/api/tools/excel/merge",    multi: true,  accept: ".xlsx,.xls,.xlsm,.csv",   hint: "Upload 2 or more Excel files — all sheets will be combined into one workbook." },
  "split":   { endpoint: "/api/tools/excel/split",    multi: false, accept: ".xlsx,.xls,.xlsm",        hint: "Upload a multi-sheet workbook — each sheet will be saved as its own file." },
};

interface SplitSheet { name: string; data: string }

function DropZone({ files, onFiles, multi, accept }: {
  files: File[]; onFiles: (f: File[]) => void; multi: boolean; accept: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function addFiles(list: FileList | null) {
    if (!list) return;
    const arr = Array.from(list);
    onFiles(multi ? [...files, ...arr] : arr);
  }

  return (
    <div
      className={cn(
        "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
        dragging ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-slate-300",
      )}
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
    >
      <input ref={inputRef} type="file" className="hidden" accept={accept} multiple={multi}
        onChange={e => addFiles(e.target.files)} />
      <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
      <p className="text-sm text-muted-foreground">
        {files.length === 0
          ? <>Click or drag {multi ? "files" : "a file"} here</>
          : <>{files.length} file{files.length > 1 ? "s" : ""} selected</>}
      </p>
    </div>
  );
}

export function ExcelToolModal({ toolId, toolName, onClose }: Props) {
  const { session } = useAuth();
  const config = TOOL_CONFIG[toolId];
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [splitSheets, setSplitSheets] = useState<SplitSheet[] | null>(null);
  const [splitBase, setSplitBase] = useState("");

  function removeFile(i: number) {
    setFiles(prev => prev.filter((_, j) => j !== i));
  }

  function downloadBase64(data: string, filename: string) {
    const link = document.createElement("a");
    link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data}`;
    link.download = filename;
    link.click();
  }

  async function run() {
    if (files.length === 0) return;
    setLoading(true);
    setError(null);
    setSplitSheets(null);
    try {
      const fd = new FormData();
      if (config.multi) files.forEach(f => fd.append("files", f));
      else fd.append("file", files[0]);

      const res = await fetch(config.endpoint, {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token}` },
        body: fd,
      });

      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || "Request failed");
      }

      if (toolId === "split") {
        const json = await res.json();
        setSplitSheets(json.sheets);
        setSplitBase(json.baseName);
        return;
      }

      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : toolId === "to-csv" ? "output.csv" : "output.xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            {toolName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{config.hint}</p>

          <DropZone files={files} onFiles={setFiles} multi={config.multi} accept={config.accept} />

          {files.length > 0 && (
            <ul className="space-y-1">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-1.5">
                  <span className="truncate max-w-[340px] text-slate-700">{f.name}</span>
                  <button onClick={() => removeFile(i)} className="ml-2 text-slate-400 hover:text-rose-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {error && <p className="text-sm text-rose-600 bg-rose-50 rounded px-3 py-2">{error}</p>}

          {splitSheets && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">
                {splitSheets.length} sheets — click each to download:
              </p>
              <div className="space-y-1">
                {splitSheets.map(s => (
                  <button
                    key={s.name}
                    onClick={() => downloadBase64(s.data, `${splitBase}_${s.name}.xlsx`)}
                    className="flex items-center gap-2 w-full text-left text-sm px-3 py-2 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-800 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5 shrink-0" />
                    {splitBase}_{s.name}.xlsx
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={run}
              disabled={files.length === 0 || loading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {loading ? "Processing…" : (
                <><Upload className="w-4 h-4 mr-1.5" /> Run</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
